<template>
  <v-card class="pa-5">
    <v-dialog v-model="showBatchModal" width="1200px">
      <v-card>
        <v-row>
          <v-col class="d-flex justify-end" cols="12">
            <v-btn icon @click="showBatchModal = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <BatchCreateForm
          :productId="form.product_id"
          @close="onBatchCreateClose"
          @batchCreated="onBatchCreated"
        />
      </v-card>
    </v-dialog>
    <v-form ref="adjustmentForm" v-model="valid">
      <v-row>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Product</p>
          </v-col>
          <v-autocomplete
            v-model="form.product_id"
            :items="products"
            item-text="product_code"
            item-value="id"
            placeholder="Select Product"
            outlined
            dense
            clearable
            :rules="[rules.required]"
            @update:search-input="(searchTerm) => onSearchProducts(searchTerm)"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Batch</p>
          </v-col>
          <v-autocomplete
            v-model="form.batch_id"
            :items="[
              { id: 'create', batch_code: 'Create New Batch' },
              ...batches,
            ]"
            item-text="batch_code"
            item-value="id"
            placeholder="Select Batch"
            outlined
            dense
            clearable
            :rules="[rules.required]"
            :disabled="!form.product_id"
            @input="onBatchChange"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Branch</p>
          </v-col>
          <v-autocomplete
            v-model="form.branch_id"
            :items="branches"
            item-text="code"
            item-value="id"
            placeholder="Select Branch"
            outlined
            dense
            clearable
            :rules="[rules.required]"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Store</p>
          </v-col>
          <v-autocomplete
            v-model="form.store_id"
            :items="stores"
            item-text="store_code"
            item-value="id"
            placeholder="Select Store"
            outlined
            dense
            clearable
            :rules="[rules.required]"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Quantity to Adjust</p>
          </v-col>
          <v-text-field
            v-model.number="form.quantity"
            placeholder="Enter Quantity to Adjust"
            type="number"
            outlined
            dense
            :rules="[rules.required, rules.positive]"
          />
        </v-col>

        <v-col cols="12">
          <v-col class="pa-0 ma-0">
            <p class="text-title">Reason for Adjustment</p>
          </v-col>
          <v-textarea
            v-model="form.reason"
            placeholder="Enter Reason for Adjustment"
            outlined
            dense
            required
            :rules="[rules.required]"
          />
        </v-col>
        <v-col cols="12">
          <v-alert v-if="error" type="error" outlined>{{ error }}</v-alert>
          <v-btn
            v-if="hasPermission('stock.add')"
            color="primary"
            :disabled="loading"
            @click="submit"
            block
            large
          >
            <v-progress-circular
              v-if="loading"
              indeterminate
              size="24"
              color="white"
            ></v-progress-circular>
            <span v-else>Submit Adjustment</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-snackbar v-model="successSnackbar" color="success">
      Adjustment submitted successfully!
      <template v-slot:action="{}">
        <v-btn color="white" text @click="successSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import { StockAdjustmentService } from "@/services/stockAdjustment-service.js";
import { RequisitionService } from "@/services/requisition-service.js";
import { StoreService } from "@/services/store-service";
import { StockTransferService } from "@/services/stockTransfer-service.js";
import BatchCreateForm from "../batches/BatchCreateForm.vue";
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("auth");

export default {
  name: "StockAdjustmentForm",
  components: {
    BatchCreateForm,
  },
  data() {
    return {
      valid: false,
      loading: false,
      error: "",
      successSnackbar: false,
      products: [],
      batches: [],
      branches: [],
      stores: [],
      availableStock: 0,
      showBatchModal: false,
      form: {
        product_id: null,
        product_code: null,
        batch_id: null,
        branch_id: null,
        quantity: null,
        adjustment_type: "Add",
        reason: "",
      },
      rules: {
        required: (v) => !!v || "This field is required.",
        positive: (v) => v > 0 || "Must be a positive number.",
      },
    };
  },
  mounted() {
    this.fetchBranches();
  },
  created() {
    this.fetchProducts();
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
  },
  watch: {
    "form.product_id"() {
      this.fetchBatches();
      this.availableStock = 0;
    },
    // "form.batch_id"() {
    //   this.fetchStock();
    // },
    "form.branch_id"() {
      this.fetchStore();
    },
  },
  methods: {
    onBatchChange(val) {
      if (val === "create") {
        this.showBatchModal = true;
      } else {
        this.form.batch_id = val;
      }
    },

    onBatchCreated() {
      this.showBatchModal = false;
      this.fetchBatches();
    },
    onBatchCreateClose() {
      this.showBatchModal = false;
      this.form.batch_id = null;
    },
    async onSearchProducts(searchTerm) {
      try {
        const res = await RequisitionService.getProducts(searchTerm);
        this.products = res.data.data;
      } catch (err) {
        this.error = "Failed to load products.";
      }
    },
    async fetchBatches() {
      this.form.batch_id = null;
      this.batches = [];
      this.availableStock = 0;
      if (!this.form.product_id) return;
      try {
        const res = await StockAdjustmentService.getBatches(
          this.form.product_id
        );
        this.batches = res.data;
      } catch (err) {
        this.error = "Failed to load batches.";
      }
    },
    async fetchBranches() {
      try {
        const branches = await StoreService.getBranches();
        this.branches = branches.data;
      } catch (error) {
        this.error = "Failed to fetch branches";
      }
    },
    async fetchStore() {
      this.form.store_id = null;
      if (!this.form.branch_id) return;
      try {
        const storesResponse = await StockTransferService.getStores(
          this.form.branch_id
        );
        this.stores = storesResponse.data;
      } catch (err) {
        this.error = "Failed to load stores.";
      }
    },

    async submit() {
      this.error = "";
      if (!this.$refs.adjustmentForm.validate()) return;

      if (this.form.adjustment_type === "Add" && this.form.quantity <= 0) {
        this.error = "Quantity must be a positive number.";
        return;
      }
      this.loading = true;
      this.products.forEach((item) => {
        if (this.form.product_id == item.id) {
          this.form.product_code = item.product_code;
        }
      });
      try {
        await StockAdjustmentService.createAdjustmentAdd({
          ...this.form,
          user_id: localStorage.getItem("userId"),
        });
        this.successSnackbar = true;

        this.form = {
          product_id: null,
          batch_id: null,
          branch_id: null,
          store_id: null,
          quantity: null,
          adjustment_type: "Add",
          reason: "",
        };
        this.batches = [];
        this.stores = [];
        this.availableStock = 0;

        this.$router.push({ name: "StockReport" });
      } catch (err) {
        this.error =
          err.response?.data?.message || "Failed to submit adjustment.";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
